import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import { useColorMode } from '../context/ColorModeContext';

function Register() {
  const navigate = useNavigate();
  const { toggleColorMode, mode } = useColorMode();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // État de vérification inline
  const [verificationPending, setVerificationPending] = useState(false);
  const [verificationUserId, setVerificationUserId] = useState(null);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const validateForm = () => {
    if (formData.password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { name, email, password } = formData;
      const response = await authService.register({ name, email, password });

      if (response.data.requiresVerification) {
        setVerificationUserId(response.data.userId);
        setVerificationEmail(response.data.email || email);
        setVerificationPending(true);
      } else {
        localStorage.setItem('era_token', response.data.token);
        localStorage.setItem('era_user', JSON.stringify(response.data.user));
        navigate('/dashboard');
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.details?.[0]?.msg ||
        t('auth.registerError')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.verifyEmail({ userId: verificationUserId, code: verificationCode });

      if (response.data.token) {
        localStorage.setItem('era_token', response.data.token);
        localStorage.setItem('era_user', JSON.stringify(response.data.user));
        navigate('/dashboard');
      } else {
        navigate('/login', {
          state: { message: 'Email vérifié avec succès. Vous pouvez vous connecter.' }
        });
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'Code de vérification invalide ou expiré.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Box sx={{ alignSelf: 'flex-end', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={toggleColorMode} color="inherit">
            {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
          <LanguageSwitcher />
        </Box>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Logo sx={{ height: 80, maxHeight: 80 }} />
            </Box>
            <Typography variant="h6" color="text.secondary">
              {verificationPending ? 'Vérification de l\'email' : t('auth.createAccount')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {verificationPending ? (
            <form onSubmit={handleVerify}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                Un code de vérification a été envoyé à <strong>{verificationEmail}</strong>.
                Veuillez l'entrer ci-dessous pour activer votre compte.
              </Typography>

              <TextField
                fullWidth
                label="Code de vérification (6 chiffres)"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                required
                margin="normal"
                autoFocus
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.2rem' }
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || verificationCode.length !== 6}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Vérifier'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('auth.fullName')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                margin="normal"
                autoFocus
              />

              <TextField
                fullWidth
                label={t('auth.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                margin="normal"
                autoComplete="email"
              />

              <TextField
                fullWidth
                label={t('auth.password')}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                margin="normal"
                helperText={t('auth.passwordHint')}
              />

              <TextField
                fullWidth
                label={t('auth.confirmPassword')}
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                margin="normal"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : t('common.register')}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2">
                  {t('auth.alreadyHaveAccount')}{' '}
                  <Link component={RouterLink} to="/login" underline="hover">
                    {t('auth.signIn')}
                  </Link>
                </Typography>
              </Box>
            </form>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default Register;
